import { useState } from "react";
import { Plus } from "lucide-react";
import { v4 } from "uuid";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { TextInputField } from "@/shared/components/field/input-field";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  ErrValidationFailedContextProvider,
  useErrValidationFailedContext,
} from "@/shared/lib/errors/err-validation-failed-context";
import { useOmniOfferCreateScopeOfWorkMutation } from "../api/offer-api";
import { Offer } from "../types/offer";
import { ScopeOfWorkBaseDataSelect } from "../../scope-of-work/components/scope-of-work-base-data-select";
import { ScopeOfWorkBaseData } from "../../scope-of-work/types/base-data";
import { ServiceVariationSelectField } from "../../servicevariation/components/service-variation-select";
import { ServiceVariation } from "../../servicevariation/types/service-variation";

/**
 * OfferScopeOfWorkAddDialogConfig configures the
 * {@link OfferScopeOfWorkAddDialog} per service kind. With selectBaseData,
 * the user selects the base data filling the new scope of work. With
 * selectServiceVariation, the user selects its service variation instead, and
 * the base data of the service variation fill it.
 */
export type OfferScopeOfWorkAddDialogConfig = {
  selectBaseData: boolean;
  selectServiceVariation: boolean;
};

/**
 * OfferScopeOfWorkAddDialog adds a new scope of work with a title to the
 * offer. Its service variation and other defaults come from the base data,
 * or the service variation is selected, see {@link OfferScopeOfWorkAddDialogConfig}. onCreated
 * receives its id.
 */
export function OfferScopeOfWorkAddDialog({
  offer,
  config,
  onCreated,
  disabled,
}: {
  offer: Offer;
  config: OfferScopeOfWorkAddDialogConfig;
  onCreated: (id: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CommonDialogTrigger
        icon={Plus}
        label=""
        variant="outline"
        disabled={disabled}
        aria-label={t("Leistung hinzufügen")}
      />
      <DialogContent>
        {/* the dialog adds a new entity, its errors stay its own */}
        <ErrValidationFailedContextProvider>
          <AddForm
            offer={offer}
            config={config}
            onCreated={(id) => {
              setOpen(false);
              onCreated(id);
            }}
          />
        </ErrValidationFailedContextProvider>
      </DialogContent>
    </Dialog>
  );
}

function AddForm({
  offer,
  config,
  onCreated,
}: {
  offer: Offer;
  config: OfferScopeOfWorkAddDialogConfig;
  onCreated: (id: string) => void;
}) {
  const [create, { isLoading, error, reset }] =
    useOmniOfferCreateScopeOfWorkMutation();
  const { write } = useErrValidationFailedContext();
  const [scopeOfWorkId] = useState(v4());
  const [title, setTitle] = useState("");
  const [baseDataId, setBaseDataId] = useState<string>();
  const [serviceVariation, setServiceVariation] = useState<ServiceVariation>(
    ServiceVariation.Zero,
  );

  const selectBaseData = (baseData?: ScopeOfWorkBaseData) => {
    setBaseDataId(baseData?.id);
    setTitle(baseData?.title ?? "");
  };

  const onAdd = () => {
    if (isLoading) {
      return;
    }
    create({ id: offer.id, scopeOfWorkId, baseDataId, serviceVariation, title })
      .unwrap()
      .then(() => {
        reset();
        onCreated(scopeOfWorkId);
      })
      .catch((e) => {
        write(e);
      });
  };

  return (
    <>
      <CommonDialogHeader
        title={t("Leistung hinzufügen")}
        description={t(
          "Die Leistung fasst Positionen zusammen, z. B. für ein Objekt oder eine Art von Arbeiten.",
        )}
      />
      {config.selectBaseData && (
        <ScopeOfWorkBaseDataSelect
          companyId={offer.companyId}
          serviceKind={offer.serviceKind}
          value={baseDataId}
          onBaseDataChange={selectBaseData}
          fieldName="baseDataId"
        />
      )}
      {config.selectServiceVariation && (
        <ServiceVariationSelectField
          serviceKind={offer.serviceKind}
          value={serviceVariation}
          onValueChange={setServiceVariation}
          fieldName={`scopeOfWork.${scopeOfWorkId}.serviceVariation`}
        />
      )}
      <TextInputField
        label={t("Titel")}
        text={title}
        onTextChange={setTitle}
        fieldName={`scopeOfWork.${scopeOfWorkId}.title`}
        debounce={false}
      />
      <RTKQueryErrorAlert error={error} />
      <CommonDialogFooter
        isLoading={isLoading}
        onClickAction={onAdd}
        actionLabel={t("Hinzufügen")}
      />
    </>
  );
}
